// Delikatne efekty dźwiękowe trybu skupienia (Web Audio API — bez plików).
// Miękkie obwiednie głośności, żeby nie były ostre. Respektuje ustawienie
// wyciszenia w localStorage. AudioContext wznawiany przy geście użytkownika
// (kliknięcie „Rozpocznij") — kolejne dźwięki działają już bez gestu.

const SOUND_KEY = "ns_focus_sound";
export const FOCUS_SOUND_EVENT = "ns:focus-sound-changed";

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

export function isFocusSoundEnabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setFocusSoundEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SOUND_KEY, enabled ? "on" : "off");
    window.dispatchEvent(new CustomEvent(FOCUS_SOUND_EVENT, { detail: { enabled } }));
    if (enabled) primeAudio();
  } catch {
    /* ignore */
  }
}

// Wywołać w geście użytkownika, żeby odblokować dźwięk na iOS/Safari.
export function primeAudio() {
  audio();
}

interface Note {
  freq: number;
  start: number; // sekundy od teraz
  duration: number;
  gain?: number;
  type?: OscillatorType;
}

function playNotes(notes: Note[]) {
  if (!isFocusSoundEnabled()) return;
  const ac = audio();
  if (!ac) return;
  const now = ac.currentTime;
  for (const n of notes) {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = n.type ?? "sine";
    osc.frequency.value = n.freq;
    const peak = n.gain ?? 0.14;
    const t0 = now + n.start;
    const t1 = t0 + n.duration;
    // Miękki atak i wybrzmienie (bez trzasków).
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t1);
    osc.connect(g);
    g.connect(ac.destination);
    osc.start(t0);
    osc.stop(t1 + 0.02);
  }
}

// Start sesji skupienia — pojedynczy, ciepły ton.
export function playFocusStart() {
  playNotes([{ freq: 440, start: 0, duration: 0.5, gain: 0.1 }]);
}

// Zbliża się przerwa — bardzo delikatny sygnał.
export function playPreBreak() {
  playNotes([{ freq: 523.25, start: 0, duration: 0.25, gain: 0.07 }]);
}

// Koniec skupienia / czas na przerwę — dwa spokojne tony w dół.
export function playSessionEnd() {
  playNotes([
    { freq: 587.33, start: 0, duration: 0.34 },
    { freq: 440, start: 0.26, duration: 0.5 },
  ]);
}

// Koniec przerwy — pogodny akord w górę (sygnał powrotu do nauki).
export function playBreakEnd() {
  playNotes([
    { freq: 523.25, start: 0, duration: 0.32 }, // C5
    { freq: 659.25, start: 0.22, duration: 0.34 }, // E5
    { freq: 783.99, start: 0.44, duration: 0.55 }, // G5
  ]);
}
